import { ENDPOINTS } from 'features/daily-plan/constants';
import { CANDIDATE_TAB_IDS } from 'features/inventory-profiles/constants';
import strings from 'strings';
import {
  CandidateProfileJobOrderColumns,
  VirtualProps as jobOrderVirtualProps
} from 'UI/components/organisms/inventoryProfiles/JobOrdersTab/candidatesJOcolumns';
import {
  CANDIDATE_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE,
  CANDIDATE_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE
} from 'UI/components/organisms/inventoryProfiles/MatchMakingTab';
import {
  candidateProfilePlacementsColumns,
  IncludesByMode
} from 'UI/components/organisms/inventoryProfiles/PlacementsTab/columns';
import {
  SENDOUTS_COLUMNS,
  SENDOUTS_RENDERERS,
  SENDOUTS_VIRTUAL_PROPS
} from 'UI/components/organisms/inventoryProfiles/SendoutsTab/columns';
import { ProfileTablesPreferenceKeys } from 'UI/components/organisms/inventoryProfiles/tabKeys';
import {
  formatTasksResponse,
  TASK_COLUMNS,
  TASKS_EXTRA_RENDERERS
} from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { defaultProfileColumnsConfig, EnvApiUrls } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  ContactEntity,
  ContactRole,
  ENTITY_TYPE_ID_MAP,
  EntityType,
  JobOrderEntity
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { filterColumnsByFeatureFlag, hasFeatureFlag } from 'UI/utils';

import {
  formatReferenceCheckResponse,
  handleJobOrderResponseChange
} from './CandidateProfileV2.utils';

const COLUMN_COPIES = strings.inventoryProfiles.sections.candidates.referenceCheckColumns;

const JOB_ORDER_INCLUDES =
  'position,specialty,address,whiteSheet,company,status,lastActivityLog,keywordTags';
const JOB_ORDER_COLUMNS = filterColumnsByFeatureFlag(CandidateProfileJobOrderColumns, [
  {
    featureFlag: FeatureFlags.JobOrderColumnsHAProfile,
    columns: ['vRecruiter.user_name', 'last_activity_date', 'whiteSheet.jobOrderType', 'status']
  }
]);

const MATCH_MAKING_ENTITY_TYPES = [
  {
    columns: CANDIDATE_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Joborder],
    endpoint: Endpoints.CandidateMatchJobOrder,
    formatResponse: CANDIDATE_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Joborder],
    id: ENTITY_TYPE_ID_MAP.jobOrder,
    isEnabled: true,
    secondaryEnpoint: Endpoints.CandidateAIMatchJobOrder,
    singular: JobOrderEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesMatchJobOrder,
    title: JobOrderEntity.title,
    type: EntityType.Joborder
  },
  {
    columns: CANDIDATE_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Contact],
    endpoint: Endpoints.CandidateMatchContact,
    formatResponse: CANDIDATE_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Contact],
    id: ContactRole.Name,
    isEnabled: true,
    singular: ContactEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesMatchContact,
    title: ContactEntity.title,
    type: EntityType.Contact
  }
];

const REFERENCE_CHECK_COLUMNS = [
  {
    name: 'id',
    options: {
      display: 'excluded'
    }
  },
  {
    name: 'name',
    label: COLUMN_COPIES.name,
    options: {
      ...defaultProfileColumnsConfig,
      sort: true
    }
  },
  {
    name: 'title',
    label: COLUMN_COPIES.title,
    options: defaultProfileColumnsConfig
  },
  {
    name: 'company',
    label: COLUMN_COPIES.company,
    options: {
      ...defaultProfileColumnsConfig,
      renderer: 'default'
    }
  },
  {
    name: 'relationship',
    label: COLUMN_COPIES.relationship,
    options: {
      ...defaultProfileColumnsConfig,
      renderer: 'default'
    }
  },
  {
    name: 'telephone',
    label: COLUMN_COPIES.phone,
    options: {
      ...defaultProfileColumnsConfig,
      renderer: {
        id: 'phoneBuilderRC'
      }
    }
  },
  {
    name: 'referenceStatus',
    label: COLUMN_COPIES.referenceStatus,
    options: {
      ...defaultProfileColumnsConfig,
      renderer: 'referenceCheckStatus'
    }
  },
  {
    name: 'created',
    label: COLUMN_COPIES.createdDate,
    options: {
      ...defaultProfileColumnsConfig,
      renderer: 'date',
      sort: true
    }
  },
  {
    name: 'updated',
    label: COLUMN_COPIES.lastUpdated,
    options: {
      display: true,
      renderer: 'date',
      sort: true
    }
  },
  {
    name: 'trace.user.fullName',
    label: COLUMN_COPIES.createdBy
  }
];

export const CANDIDATE_TABS_CONFIG = {
  [CANDIDATE_TAB_IDS.MatchMaking]: {
    availableEntityTypesToMatch: MATCH_MAKING_ENTITY_TYPES,
    DataTableProps: { apiVersion: 1 }
  },
  [CANDIDATE_TAB_IDS.JobOrders]: {
    endpoint: Endpoints.JobOrders,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesJobOrders,
    DataTableProps: {
      includes: hasFeatureFlag(FeatureFlags.JobOrderColumnsHAProfile)
        ? `${JOB_ORDER_INCLUDES},vRecruiter`
        : JOB_ORDER_INCLUDES,
      apiVersion: 1,
      virtualProps: jobOrderVirtualProps,
      formatResponse: handleJobOrderResponseChange,
      orderByOptions: { column: 'created_at', direction: 'desc' }
    },
    initialColumns: JOB_ORDER_COLUMNS
  },
  [CANDIDATE_TAB_IDS.Sendouts]: {
    endpoint: Endpoints.Sendouts,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesSendouts,
    initialColumns: SENDOUTS_COLUMNS,
    extraRenderers: SENDOUTS_RENDERERS,
    DataTableProps: {
      apiVersion: 1,
      virtualProps: SENDOUTS_VIRTUAL_PROPS,
      includes:
        'type,status,interviews,hiringAuthorithies,jobOrderAccountable,candidateAccountable,joborder,candidate,jobOrderAccountableCoach,candidateAccountableCoach'
    }
  },
  [CANDIDATE_TAB_IDS.Placements]: {
    endpoint: Endpoints.Placements,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesPlacement,
    initialColumns: candidateProfilePlacementsColumns,
    DataTableProps: {
      includes: IncludesByMode.candidate,
      apiVersion: 2
    }
  },
  [CANDIDATE_TAB_IDS.ReferenceChecks]: {
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesReferenceChecks,
    initialColumns: REFERENCE_CHECK_COLUMNS,
    DataTableProps: {
      endpoint: `${EnvApiUrls.API_URL_AWS}/${Endpoints.ReferenceCheckByCandidate}`,
      httpHeaders: {
        ...(EnvApiUrls.SEND_API_KEY && { 'x-api-key': EnvApiUrls.API_KEY_AWS })
      },
      formatResponse: formatReferenceCheckResponse
    }
  },
  [CANDIDATE_TAB_IDS.ActionPlan]: {
    DataTableProps: {
      apiVersion: 1,
      endpoint: ENDPOINTS.tasks,
      formatResponse: formatTasksResponse
    },
    entityType: EntityType.Candidate,
    entityTypeId: ENTITY_TYPE_ID_MAP.candidate,
    extraRenderers: TASKS_EXTRA_RENDERERS,
    initialColumns: TASK_COLUMNS,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesTasks
  }
};
